import { AppHeader } from '@/components/shared/AppHeader';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { CheckCircle2, XCircle, AlertCircle, RefreshCw } from 'lucide-react';
import { fornecedoresMock } from '@/lib/data/mock-data';
import { formatCurrency } from '@/lib/utils/formatters';

export function FornecedoresPage() {
  const getStatusBadge = (status: string) => {
    const variants = {
      REGULAR: {
        variant: 'default' as const,
        icon: CheckCircle2,
        text: 'Regular',
        className: 'bg-green-500',
      },
      IRREGULAR: {
        variant: 'destructive' as const,
        icon: XCircle,
        text: 'Irregular',
        className: 'bg-red-500',
      },
      PENDENTE: {
        variant: 'secondary' as const,
        icon: AlertCircle,
        text: 'Pendente',
        className: 'bg-yellow-500',
      },
      VALIDA: {
        variant: 'default' as const,
        icon: CheckCircle2,
        text: 'Válida',
        className: 'bg-green-500',
      },
      VENCIDA: {
        variant: 'destructive' as const,
        icon: XCircle,
        text: 'Vencida',
        className: 'bg-red-500',
      },
      NAO_EMITIDA: {
        variant: 'secondary' as const,
        icon: AlertCircle,
        text: 'Não Emitida',
        className: 'bg-gray-500',
      },
      ADIMPLENTE: {
        variant: 'default' as const,
        icon: CheckCircle2,
        text: 'Adimplente',
        className: 'bg-green-500',
      },
      INADIMPLENTE: {
        variant: 'destructive' as const,
        icon: XCircle,
        text: 'Inadimplente',
        className: 'bg-red-500',
      },
      ATIVA: {
        variant: 'default' as const,
        icon: CheckCircle2,
        text: 'Ativa',
        className: 'bg-green-500',
      },
      SUSPENSA: {
        variant: 'destructive' as const,
        icon: XCircle,
        text: 'Suspensa',
        className: 'bg-red-500',
      },
      INAPTA: {
        variant: 'destructive' as const,
        icon: XCircle,
        text: 'Inapta',
        className: 'bg-orange-500',
      },
      BAIXADA: {
        variant: 'secondary' as const,
        icon: AlertCircle,
        text: 'Baixada',
        className: 'bg-gray-500',
      },
    };
    const config = variants[status as keyof typeof variants] || variants.PENDENTE;
    const Icon = config.icon;
    return (
      <Badge variant={config.variant} className={config.className}>
        <Icon className="h-3 w-3 mr-1" />
        {config.text}
      </Badge>
    );
  };

  const totalFornecedores = fornecedoresMock.length;
  const fornecedoresRegulares = fornecedoresMock.filter(
    f =>
      f.regularidadeFiscal.receitaFederal.status === 'REGULAR' &&
      f.inadimplencia.status === 'ADIMPLENTE'
  ).length;
  const fornecedoresIrregulares = fornecedoresMock.filter(
    f =>
      f.regularidadeFiscal.receitaFederal.status === 'IRREGULAR' ||
      f.inadimplencia.status === 'INADIMPLENTE'
  ).length;
  const creditosAprovados = fornecedoresMock.filter(f => f.validacaoCredito.aprovado).length;

  return (
    <div className="min-h-screen bg-background">
      <AppHeader title="Conhecendo Meus Fornecedores" subtitle="Validação de regularidade fiscal" />

      <main className="w-full px-6 py-8">
        <div className="mb-8">
          <h2 className="text-3xl font-bold text-foreground text-balance mb-2">
            Conhecendo Meus Fornecedores
          </h2>
          <p className="text-muted-foreground text-pretty">
            Integração com Receita Federal para validação de regularidade fiscal e créditos
            tributários
          </p>
        </div>

        {/* Cards de Resumo */}
        <div className="grid gap-6 md:grid-cols-4 mb-8">
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Total de Fornecedores</CardDescription>
              <CardTitle className="text-3xl">{totalFornecedores}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Fornecedores Regulares</CardDescription>
              <CardTitle className="text-3xl text-green-600">{fornecedoresRegulares}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Fornecedores Irregulares</CardDescription>
              <CardTitle className="text-3xl text-red-600">{fornecedoresIrregulares}</CardTitle>
            </CardHeader>
          </Card>
          <Card>
            <CardHeader className="pb-3">
              <CardDescription>Créditos Aprovados</CardDescription>
              <CardTitle className="text-3xl text-primary">{creditosAprovados}</CardTitle>
            </CardHeader>
          </Card>
        </div>

        {/* Tabela de Fornecedores */}
        <Card>
          <CardHeader>
            <div className="flex items-center justify-between">
              <div>
                <CardTitle>Lista de Fornecedores</CardTitle>
                <CardDescription>
                  Validação automática com Receita Federal e análise de créditos fiscais
                </CardDescription>
              </div>
              <Button variant="outline" size="sm">
                <RefreshCw className="h-4 w-4 mr-2" />
                Atualizar Consultas
              </Button>
            </div>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Fornecedor</TableHead>
                  <TableHead>CNPJ</TableHead>
                  <TableHead>Situação Cadastral</TableHead>
                  <TableHead>Receita Federal</TableHead>
                  <TableHead>Inadimplência</TableHead>
                  <TableHead>Crédito Fiscal</TableHead>
                  <TableHead className="text-right">Total Compras</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {fornecedoresMock.map(fornecedor => (
                  <TableRow key={fornecedor.id}>
                    <TableCell>
                      <div>
                        <p className="font-medium">{fornecedor.razaoSocial}</p>
                        <p className="text-xs text-muted-foreground">{fornecedor.nomeFantasia}</p>
                      </div>
                    </TableCell>
                    <TableCell className="font-mono text-sm">{fornecedor.cnpj}</TableCell>
                    <TableCell>{getStatusBadge(fornecedor.situacaoCadastral)}</TableCell>
                    <TableCell>
                      {getStatusBadge(fornecedor.regularidadeFiscal.receitaFederal.status)}
                    </TableCell>
                    <TableCell>{getStatusBadge(fornecedor.inadimplencia.status)}</TableCell>
                    <TableCell>
                      {fornecedor.validacaoCredito.aprovado ? (
                        <Badge className="bg-green-500">
                          <CheckCircle2 className="h-3 w-3 mr-1" />
                          Aprovado
                        </Badge>
                      ) : (
                        <Badge variant="destructive">
                          <XCircle className="h-3 w-3 mr-1" />
                          Reprovado
                        </Badge>
                      )}
                    </TableCell>
                    <TableCell className="text-right font-semibold">
                      {formatCurrency(fornecedor.estatisticas.totalCompras)}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </main>
    </div>
  );
}
